import {
  NavigationActionType,
  NavigationState,
  START_NAVIGATION,
  STOP_NAVIGATION,
  UPDATE_NAVIGATION,
  INIT_NAVIGATION,
  MapState,
  MapActionType,
  UPDATE_MAP_INITILIZED,
  UPDATE_MAP_ROUTE_OVERVIEW,
  SWITCH_MAP_STYLE,
  SET_NAVIGATION_ROUTE,
  PoiState,
  PoiActionType,
  CHANGE_NAVIGATION_OVERLAY,
  SET_ACTIVE_POI_ITEM,
  UPDATE_POI_STACK_LIST_VISIBLE,
  SHOW_NO_ROUTES,
  UPDATE_DESTINATION_INPUT_ACTIVE,
  ACTIVATE_ALTERNATIVE_ROUTES_OVERVIEW,
  ACTIVATE_DEST_INPUT_OVERVIEW,
  UPDATE_SEARCH_AREA_SCREEN_VISIBILITY,
  UPDATE_SHOW_ALTERNATIVE_ROUTES,
  SET_SELECTED_EVENT,
  UPDATE_SHOW_MINIMIZE_MAP_BUTTON,
  EXPAND_ROUTE_MONITOR,
  SET_RANGE_MODE_ACTIVE,
  MAP_SET_ZOOM,
  MAP_ZOOM_IN,
  MAP_ZOOM_OUT
} from "./types"

import { ENavigationOverlay } from "./navigationOverlay/NavigationOverlay"
import NavigationService from "@pag/utils/navigationService/NavigationService"

const initialNavigationState: NavigationState = {
  alternativeRoutes: [],
  activeRouteId: undefined,
  currentPosition: NavigationService.getActiveConfig().initialPosition,
  isActive: false,
  bearing: 64.2445556105929,
  speed: 0,
  zoom: 10,
  nextManeuver: undefined,
  futureManeuver: undefined,
  remainingDistance: undefined,
  remainingDuration: undefined
}

const initialMapState: MapState = {
  isMapInitialized: false,
  isRouteOverViewActive: true,
  style: "day",
  showNoRoutes: true,
  isDestinationInputActive: false,
  isSearchAreaScreenVisible: false,
  showAlternativeRoutes: false,
  selectedEvent: undefined,
  showMinimizeMapButton: false,
  expandRouteMonitor: true,
  rangeModeActive: false
}

const initialPoiState: PoiState = {
  overlay: ENavigationOverlay.GUIDANCE_ACTIVE,
  activeItemIndex: undefined,
  poiStackListVisible: false
}

export function navigationReducer(state = initialNavigationState, action: NavigationActionType): NavigationState {
  switch (action.type) {
    case START_NAVIGATION: {
      if (state.activeRouteId) {
        NavigationService.start(state.activeRouteId)
      }
      return {
        ...state,
        isActive: true
      }
    }

    case SET_NAVIGATION_ROUTE:
      return {
        ...state,
        activeRouteId: action.payload.routeId
      }

    case STOP_NAVIGATION:
      return {
        ...state,
        currentPosition: initialNavigationState.currentPosition,
        isActive: initialNavigationState.isActive,
        bearing: initialNavigationState.bearing,
        speed: initialNavigationState.speed,
        zoom: initialNavigationState.zoom,
        nextManeuver: initialNavigationState.nextManeuver,
        remainingDistance: initialNavigationState.remainingDistance,
        remainingDuration: initialNavigationState.remainingDuration
      }

    case UPDATE_NAVIGATION:
      return {
        ...state,
        currentPosition: action.payload.currentPosition,
        bearing: action.payload.bearing,
        speed: action.payload.speed,
        zoom: action.payload.zoom,
        isActive: true,
        remainingDistance: action.payload.remainingDistance,
        nextManeuver: action.payload.nextManeuver,
        futureManeuver: action.payload.futureManeuver,
        remainingDuration: action.payload.remainingDuration
      }

    case INIT_NAVIGATION:
      return {
        ...state,
        activeRouteId: action.payload.activeRouteId,
        alternativeRoutes: action.payload.alternativeRoutes,
        currentPosition: action.payload.currentPosition,
        bearing: action.payload.bearing,
        speed: action.payload.speed
      }

    case MAP_SET_ZOOM:
      return {
        ...state,
        zoom: state.zoom
      }

    case MAP_ZOOM_IN:
      return {
        ...state,
        zoom: state.zoom + 1
      }

    case MAP_ZOOM_OUT:
      return {
        ...state,
        zoom: Math.max(state.zoom - 1, 10.824313176416563) // @NOTE(kirill): Based on bounds.
      }

    default:
      return state
  }
}

export function mapReducer(state = initialMapState, action: MapActionType): MapState {
  switch (action.type) {
    case UPDATE_MAP_INITILIZED:
      return {
        ...state,
        isMapInitialized: action.payload.isMapInitialized
      }
    case UPDATE_MAP_ROUTE_OVERVIEW:
      return {
        ...state,
        isRouteOverViewActive: action.payload
      }
    case START_NAVIGATION:
      return {
        ...state,
        isRouteOverViewActive: false,
        isDestinationInputActive: false,
        showAlternativeRoutes: false,
        showNoRoutes: false
      }
    case STOP_NAVIGATION:
      return {
        ...state,
        isRouteOverViewActive: true,
        showAlternativeRoutes: false,
        showNoRoutes: true,
        selectedEvent: undefined
      }
    case SWITCH_MAP_STYLE:
      return {
        ...state,
        style: state.style === "day" ? "night" : "day"
      }
    case SHOW_NO_ROUTES: {
      return {
        ...state,
        showNoRoutes: action.payload
      }
    }
    case UPDATE_DESTINATION_INPUT_ACTIVE: {
      return {
        ...state,
        isDestinationInputActive: action.payload
      }
    }
    case ACTIVATE_ALTERNATIVE_ROUTES_OVERVIEW: {
      return {
        ...state,
        isDestinationInputActive: false,
        isRouteOverViewActive: true,
        showNoRoutes: false,
        showAlternativeRoutes: true
      }
    }
    case ACTIVATE_DEST_INPUT_OVERVIEW: {
      return {
        ...state,
        isDestinationInputActive: true,
        showNoRoutes: true,
        showAlternativeRoutes: false
      }
    }
    case UPDATE_SHOW_ALTERNATIVE_ROUTES: {
      return {
        ...state,
        showAlternativeRoutes: action.payload
      }
    }
    case UPDATE_SEARCH_AREA_SCREEN_VISIBILITY: {
      return {
        ...state,
        isDestinationInputActive: !action.visible,
        isSearchAreaScreenVisible: action.visible
      }
    }
    case SET_SELECTED_EVENT: {
      return {
        ...state,
        selectedEvent: action.payload
      }
    }
    case UPDATE_SHOW_MINIMIZE_MAP_BUTTON: {
      return {
        ...state,
        showMinimizeMapButton: action.show
      }
    }
    case EXPAND_ROUTE_MONITOR: {
      return {
        ...state,
        expandRouteMonitor: action.payload
      }
    }
    case SET_RANGE_MODE_ACTIVE: {
      return {
        ...state,
        rangeModeActive: action.payload
      }
    }
    default:
      return state
  }
}

export function poiReducer(state = initialPoiState, action: PoiActionType): PoiState {
  switch (action.type) {
    case CHANGE_NAVIGATION_OVERLAY:
      return {
        ...state,
        overlay: action.payload
      }
    case SET_ACTIVE_POI_ITEM:
      return {
        ...state,
        activeItemIndex: action.payload
      }
    case UPDATE_POI_STACK_LIST_VISIBLE:
      return {
        ...state,
        poiStackListVisible: action.payload
      }
    case ACTIVATE_DEST_INPUT_OVERVIEW:
      return initialPoiState
    default:
      return state
  }
}
